import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AcceptUserProps } from './accept-user.types.ts';
import '@/assets/styles/components/accept-user.styles.scss';
import { Button } from '@/components';
import { isNullOrUndefined } from '@/utility';
import { useMeeting } from '@/context/meeting-context';
import { useAccurateCountdown, useCountdown } from '@/utility/use-util.tsx';
import clsx from 'clsx';
import { useDevicePermissions } from '@/context/device-permission-context.tsx';

const CIRCLE_LENGTH = 2 * Math.PI * 18;

const AcceptUser: React.FC<AcceptUserProps> = ({ messageStyle, videoCallType }) => {
	const [selectedImage, setSelectedImage] = useState<string | null>(null);

	const { onApprove, socketData, isAcceptedCall, onStartCall, hasActiveMeeting, onConnectPastMeeting, handleNoResponseMatch, isAcceptedCallLoading } = useMeeting();
	const { controlPermission } = useDevicePermissions();

	const { handleMatch, meetingCreated, matchApproved, matchDeadline } = socketData;
	const totalSeconds = Math.floor((handleMatch?.matchingAcceptDuration || 0) / 1000);

	const meetingPreparationTimer = useAccurateCountdown();
	const matchingAcceptDurationTimer = useCountdown(handleMatch?.matchingAcceptDuration);

	const timerStartedRef = useRef(false);
	const noResponseCalledRef = useRef(false);

	useEffect(() => {
		if (matchApproved?.meetingPreparationCompletionDate && isAcceptedCall) {
			meetingPreparationTimer?.reset(matchApproved.meetingPreparationCompletionDate);
		}
	}, [matchApproved?.meetingPreparationCompletionDate, meetingCreated?.meetingId, isAcceptedCall]);

	useEffect(() => {
		if (!matchDeadline) return;

		const remaining = matchDeadline - Date.now();

		if (remaining > 0) {
			timerStartedRef.current = true;
			noResponseCalledRef.current = false;
			matchingAcceptDurationTimer.reset(remaining);
		} else {
			noResponseCalledRef.current = true;
			handleNoResponseMatch();
		}
	}, [matchDeadline]);

	useEffect(() => {
		if (!matchingAcceptDurationTimer?.isCompleted || !timerStartedRef.current || isAcceptedCall || noResponseCalledRef.current) {
			return;
		}

		noResponseCalledRef.current = true;
		handleNoResponseMatch();
	}, [matchingAcceptDurationTimer?.isCompleted, isAcceptedCall]);

	useEffect(() => {
		return () => {
			timerStartedRef.current = false;
			noResponseCalledRef.current = false;
		};
	}, [handleMatch?.matchId]);


	const getCallType = () => {
		if (isNullOrUndefined(handleMatch?.meetingSupportType)) return null;
		return handleMatch?.meetingSupportType === 'PROBLEM_SOLVING' ? 'Soru Çözümü' : 'Konu Anlatımı';
	};

	const callType = getCallType();

	const getStrokeDashoffset = useCallback(
		(currentSeconds: number) => {
			const progress = (currentSeconds / totalSeconds) * 100;
			return CIRCLE_LENGTH - (progress / 100) * CIRCLE_LENGTH;
		},
		[totalSeconds]
	);

	const handleStartCall = () => {
		if (hasActiveMeeting) {
			onConnectPastMeeting();
			return;
		}
		onStartCall();
	};

	const getQuestionImgUrl = (image: any) => {
		return image?.baseUrl + '/' + image?.path + '/' + image?.filename;
	};

	const showCircleTimer = timerStartedRef.current && !matchingAcceptDurationTimer.isCompleted && !isAcceptedCall;

	return (
		<div className="accept-user-container">
			{showCircleTimer && (
				<div className="timer-container">
					<svg
						className="timer-svg"
						viewBox="0 0 48 48">
						<circle
							cx="24"
							cy="24"
							r="18"
							fill="none"
							stroke="var(--text-000000)"
							strokeWidth="6"
							strokeOpacity="0.15"
						/>
						<circle
							cx="24"
							cy="24"
							r="18"
							fill="none"
							stroke="var(--text-3a68bc)"
							strokeWidth="6"
							strokeLinecap="round"
							strokeDasharray={CIRCLE_LENGTH}
							strokeDashoffset={getStrokeDashoffset(matchingAcceptDurationTimer.remainingSeconds)}
							transform="rotate(-90 24 24)"
						/>
					</svg>
					<span className="timer-text">{matchingAcceptDurationTimer.remainingSeconds}</span>
				</div>
			)}

			{callType && (
				<div
					className={clsx('call-type-container', {
						'call-type-subject-container': videoCallType === 'SUBJECT_EXPLANATION'
					})}>
					<p>{callType}</p>
				</div>
			)}

			<div className="user-information-container">
				<img
					src={handleMatch?.studentAvatarUrl}
					className="user-img"
					alt={handleMatch?.studentName}
				/>
				<h1>{handleMatch?.studentName + ' ' + handleMatch?.studentSurname}</h1>
				<p>{handleMatch?.studentPreferredGradeName}</p>
			</div>

			<div className="video-call-info-container">
				{handleMatch?.lessonName && <p className="lesson-name">{handleMatch?.lessonName}</p>}
				<p className={`message ${messageStyle}`}>{handleMatch?.topicName}</p>
			</div>

			{handleMatch?.attachmentInfos?.length > 0 && (
				<div className="image-gallery-container">
					{handleMatch?.attachmentInfos?.map((image, index) => (
						<div
							key={index}
							className="gallery-image-wrapper"
							onClick={() => setSelectedImage(getQuestionImgUrl(image))}>
							<img
								src={getQuestionImgUrl(image)}
								alt={`Image ${index + 1}`}
								className="gallery-image"
							/>
							<div className="zoom-icon">
								<i className={'icons-magnifer-zoom-in'} />
							</div>
						</div>
					))}
				</div>
			)}

			{selectedImage && (
				<div
					className="accept-user-modal-overlay"
					onClick={() => setSelectedImage(null)}>
					<div className="modal-content">
						<img src={selectedImage} />
						<button
							className="modal-close"
							onClick={() => setSelectedImage(null)}>
							<i className={'icons-close'} />
						</button>
					</div>
				</div>
			)}

			{!isAcceptedCall && (
				<Button
					className="accept-button"
					disabled={isAcceptedCallLoading}
					onClick={onApprove}>
					Kabul Et
				</Button>
			)}

			{isAcceptedCall && (
				<>
					<Button
						className="accept-button"
						variant={'success'}
						onClick={() => controlPermission(handleStartCall)}
						disabled={!(meetingCreated || hasActiveMeeting)}>
						{!meetingPreparationTimer?.isCompleted ? (
							<>
								<b>{meetingPreparationTimer?.formattedTime}</b>
								<span style={{ fontWeight: 200 }}> sn</span>
							</>
						) : (
							<span>Şimdi Bağlan</span>
						)}
					</Button>

					{!meetingPreparationTimer?.isCompleted && meetingCreated && (
						<button
							className={'connect-now-button'}
							onClick={() => controlPermission(handleStartCall)}>
							<p>Şimdi Bağlan</p>
						</button>
					)}
				</>
			)}
		</div>
	);
};

export default AcceptUser;
